import { onUnmounted, watch } from 'vue';
import type { LineString } from 'geojson';
import {
  createVehiclePlacementEstimator,
  resolveVehicleTrack,
  type StopLike,
  type TripLike,
  type VehiclePlacement,
  type VehicleStatus,
} from '@pysae/transit-core/vehicle-placement';
import type { OnlineDevice } from '@/@types/device';
import type { Geometry } from '@/@types/gtfs-ressource';
import type { MapVehicle } from '@/@types/map';
import { isAppVisible } from '@/libs/app-visibility';
import { useIsActive } from '@/use/active';

/** ~10 frames per second, like Op: smooth enough for a bus, cheap enough for a busy line. */
export const FRAME_MS = 100;

type VehicleAnimation = NonNullable<MapVehicle['animation']>;
type VehiclePlacements = ReadonlyMap<string, VehiclePlacement>;

export interface AnimationGtfs {
  trips: Record<string, TripLike>;
  shapes: Record<string, { geometry: Geometry }>;
  stops: Record<string, StopLike>;
}

function lineStringOf(geometry: Geometry | undefined): LineString | undefined {
  return geometry?.type === 'LineString' ? (geometry as LineString) : undefined;
}

/**
 * What the estimator needs to animate `device`; undefined keeps it drawn at its raw position,
 * as before the animation: no `ts` (API older than api#3477), no usable trip shape, or a trip
 * shown deviated (`deviatedTripId`), whose GTFS shape is no longer the one on the map.
 */
export function vehicleAnimationOf(
  device: OnlineDevice,
  gtfs: AnimationGtfs,
  deviatedTripId?: string,
): VehicleAnimation | undefined {
  if (device.ts == null || device.latlng.length < 2 || !device.trip_id) return undefined;
  if (device.trip_id === deviatedTripId) return undefined;
  const trip = gtfs.trips[device.trip_id];
  // The shape object of the store, stable across polls: it keys the estimator's geometry cache.
  const shape = lineStringOf(trip && gtfs.shapes[trip.shape_id]?.geometry);
  const track = resolveVehicleTrack(
    { tripId: device.trip_id, currentStopSequence: device.current_stop_sequence },
    trip,
    shape,
    gtfs.stops,
  );
  if (!track) return undefined;
  return {
    track,
    report: {
      latlng: [device.latlng[0], device.latlng[1]],
      ts: device.ts,
      bearing: device.bearing,
      status: device.current_status as VehicleStatus | undefined,
      isOffItinerary: Boolean(device.off_itinerary || device.trip_pending),
    },
  };
}

/**
 * Animates the vehicles between the positions polled from the API: `render` receives, at each
 * frame while the app is visible, the page active and a vehicle animated, where to draw them. A vehicle absent from
 * the placements is drawn at its raw position.
 */
export function useVehicleAnimation(options: {
  vehicles: () => Record<string, Pick<MapVehicle, 'animation'>>;
  render: (placements: VehiclePlacements) => void;
}) {
  const estimator = createVehiclePlacementEstimator();
  const isActive = useIsActive();
  let frameRequest: number | undefined;
  let lastFrameMs = -Infinity;
  let hasPlacements = false;
  let isAnimating = false;

  function draw(placements: VehiclePlacements) {
    // Redrawing an empty result on every frame would rebuild an unchanged source for nothing.
    if (placements.size === 0 && !hasPlacements) return;
    hasPlacements = placements.size > 0;
    options.render(placements);
  }

  function renderFrame(nowMs: number) {
    lastFrameMs = nowMs;
    draw(estimator.sample(nowMs));
  }

  function onAnimationFrame() {
    frameRequest = requestAnimationFrame(onAnimationFrame);
    // The estimator's clock is the one `push` gets, not the frame timestamp.
    const nowMs = Date.now();
    if (nowMs - lastFrameMs >= FRAME_MS) renderFrame(nowMs);
    // Detached vehicles have glided back onto their raw position: nothing left to draw.
    if (!isAnimating && !hasPlacements) stopLoop();
  }

  function ingest(vehicles: Record<string, Pick<MapVehicle, 'animation'>>) {
    const nowMs = Date.now();
    estimator.retain(new Set(Object.keys(vehicles)));
    Object.entries(vehicles).forEach(([vehicleId, { animation }]) =>
      estimator.push(vehicleId, animation?.report, animation?.track, nowMs),
    );
    isAnimating = Object.values(vehicles).some(({ animation }) => animation);
    syncLoop();
    // Drawn at once, not at the next frame: a new raw fix must not flash before its glide.
    if (frameRequest !== undefined) renderFrame(nowMs);
  }

  function stopLoop() {
    if (frameRequest === undefined) return false;
    cancelAnimationFrame(frameRequest);
    frameRequest = undefined;
    lastFrameMs = -Infinity;
    estimator.resetDisplayed();
    return true;
  }

  function syncLoop() {
    // No wake-up on every screen refresh without anything to glide (home map, API without `ts`).
    if (isAppVisible.value && isActive.value && (isAnimating || hasPlacements)) {
      frameRequest ??= requestAnimationFrame(onAnimationFrame);
    } else if (stopLoop()) {
      draw(new Map());
    }
  }

  watch(options.vehicles, ingest, { immediate: true });
  watch([isAppVisible, isActive], syncLoop, { immediate: true });
  onUnmounted(stopLoop);
}
